import { useAuth } from "@/features/auth/hooks/useAuth";
import { IS_DEV } from "@/features/config";
import { useFeatureFlags } from "@/features/config/useFeatureFlags";
import { useAtomValue } from "jotai";
import { datasetModeAtom, datasetModeRequest, type DatasetModeAccess } from "../datasetMode";

/** The fast, accuracy (shown as Comprehensive), and advanced modes need this flag. */
export const useCanUseMapModels = (): boolean => {
  const { flags } = useFeatureFlags();
  return IS_DEV || flags.paperclipMapModels;
};

/** The comprehensive mode (shown as Full read) follows the GXL routines, so it needs a GXL account with the routines flag. */
export const useCanUseGxlModes = (): boolean => {
  const { flags } = useFeatureFlags();
  const { user } = useAuth();
  const email = user?.email?.trim().toLowerCase() ?? "";
  return email.endsWith("@gxl.ai") && (IS_DEV || flags.routines);
};

/** Fast mode from the dataset builder service. */
export const useCanUseFastBuilder = (): boolean => {
  const { flags } = useFeatureFlags();
  return flags.datasetBuilder;
};

export const useDatasetModeAccess = (): DatasetModeAccess => ({
  mapModels: useCanUseMapModels(),
  gxl: useCanUseGxlModes(),
  fastBuilder: useCanUseFastBuilder(),
});

/** The `dataset_mode` that a Dataset builder chat message sends now. */
export const useDatasetModeRequest = (): Record<string, unknown> | undefined => {
  const state = useAtomValue(datasetModeAtom);
  return datasetModeRequest(state, useDatasetModeAccess());
};
